import emailjs from '@emailjs/browser';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

function ContactForm() {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    topic: '',
    contactMethod: '',
    contactInfo: '',
    consultationTime: '',
    additionalInfo: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [token, setToken] = useState(null);
  const captchaRef = useRef(null);

  const topics = [
    { value: 'Real Estate Investment Consulting', label: t('consult.topics.feasibility') },
    { value: 'Investment Structural Design', label: t('consult.topics.planning') },
    { value: 'Project Development and Management', label: t('consult.topics.construction') },
    { value: 'Residential home building advice', label: t('consult.topics.portfolio') },
    { value: 'First Home Buyers', label: t('consult.topics.custom') },
    { value: 'Extensive Land and Old House Revitalization', label: t('consult.topics.extensive') }
  ];

  const contactMethods = [
    { value: 'email', label: t('consult.contact.email') },
    { value: 'phone', label: t('consult.contact.phone') }
  ];

  const onVerify = (token) => {
    setToken(token);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!token) {
      alert(t("contact_form.please_verify_captcha"));
      return;
    }

    // Prepare the email template data
    const templateParams = {
      fullname: formData.name,
      topic: topics.find(t => t.value === formData.topic)?.label || formData.topic,
      consultation_time: formData.consultationTime,
      page_info: window.location.href,
      message: formData.additionalInfo
    };

    // Add contact info based on selected method
    if (formData.contactMethod === 'email') {
      templateParams.email = formData.contactInfo;
      templateParams.phone = '';
    } else {
      templateParams.phone = formData.contactInfo;
      templateParams.email = '';
    }

    emailjs
      .send(
        'service_0qn969g',
        'template_xzim6i9',
        templateParams,
        'PcHzeyHQ7091mGvgC'
      )
      .then((result) => {
        console.log(result.text);
        if (result.text === "OK") {
          setFormData({
            name: '',
            topic: '',
            contactMethod: '',
            contactInfo: '',
            consultationTime: '',
            additionalInfo: ''
          });
          setShowSuccess(true);
          // Reset captcha after successful submission
          captchaRef.current.resetCaptcha();
          setToken(null);

          // Hide success message after 5 seconds
          setTimeout(() => {
            setShowSuccess(false);
          }, 10000);
        }
      },
        (error) => {
          console.log(error.text);
          alert(t("contact_form.error_message"));
        });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const FormContainer = styled.div`
    padding: 40px;
    background: #111111;
    border-radius: 10px;
    box-shadow: 0 0 20px rgba(0,0,0,0.3);
`;

  const FormGroup = styled.div`
    margin-bottom: 25px;
`;

  const Label = styled.label`
    display: block;
    margin-bottom: 8px;
    color: #ffffff;
    font-weight: 500;
    .required {
        color: #ff6b6b;
        margin-right: 4px;
    }
`;

  const commonInputStyles = `
    width: 100%;
    height: 50px !important;
    padding: 0 15px;
    border: 1px solid #333333;
    border-radius: 6px;
    font-size: 16px;
    line-height: 50px;
    box-sizing: border-box;
    background-color: #222222;
    color: #ffffff;
    &:focus {
        outline: none;
        border-color: #0F7E8D;
    }
`;

  const Input = styled.input`
    ${commonInputStyles}
    &[type="datetime-local"] {
        line-height: normal;
    }
    &::placeholder {
        color: #999999;
    }
`;

  const TextArea = styled.textarea`
    width: 100%;
    padding: 15px;
    border: 1px solid #333333;
    border-radius: 6px;
    font-size: 16px;
    box-sizing: border-box;
    background-color: #222222;
    color: #ffffff;
    min-height: 150px;
    line-height: 1.5;
    &:focus {
        outline: none;
        border-color: #0F7E8D;
    }
    &::placeholder {
        color: #999999;
    }
`;

  const Select = styled.select`
    ${commonInputStyles}
    padding-right: 30px;
    appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ffffff' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 15px center;
    background-size: 12px;
    cursor: pointer;
    
    option {
        background-color: #222222;
        color: #ffffff;
    }
`;

  const QuestionText = styled.p`
    color: #ffffff;
    font-size: 16px;
    margin-bottom: 12px;
    font-weight: 500;
`;

  const PrivacyText = styled.p`
    color: #999999;
    font-size: 14px;
    text-align: center;
    margin: 20px 0;
`;

  const SubmitButton = styled.button`
    background-color: #0F7E8D;
    color: white;
    padding: 15px 30px;
    border: none;
    border-radius: 6px;
    font-size: 18px;
    cursor: pointer;
    width: 100%;
    transition: background-color 0.3s;
    &:hover {
        background-color:rgb(13, 144, 161);
    }
`;

  const SuccessMessage = styled.div`
    background-color: #4CAF50;
    color: white;
    padding: 20px;
    border-radius: 6px;
    text-align: center;
    margin-top: 20px;
`;

  return (
    <div style={{
      width: '100vw',
      padding: '5rem 0',
      backgroundColor: '#000000',
      color: 'white'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 2rem'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem'
        }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            marginBottom: '1rem'
          }}>Contact Us</h2>
          <p style={{ color: '#D1D5DB' }}>Leverage Big Data to Understand the Market and Drive Your Investment Success</p>
        </div>

        {/* 联系信息卡片 */}


        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '2rem',
          alignItems: 'start'
        }}>
          {/* Contact Form */}
          <FormContainer>
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label><span className="required">*</span>{t('consult.form.name')}</Label>
                <Input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('consult.form.namePlaceholder')}
                />
              </FormGroup>

              <FormGroup>
                <Label><span className="required">*</span>{t('consult.form.topic')}</Label>
                <Select
                  name="topic"
                  required
                  value={formData.topic}
                  onChange={handleChange}
                >
                  <option value="">{t('consult.form.selectPlaceholder')}</option>
                  {topics.map(topic => (
                    <option key={topic.value} value={topic.value}>
                      {topic.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <QuestionText>
                {t('consult.form.contactQuestion')}
              </QuestionText>

              <FormGroup>
                <Label><span className="required">*</span>{t('consult.form.contactMethod')}</Label>
                <Select
                  name="contactMethod"
                  required
                  value={formData.contactMethod}
                  onChange={handleChange}
                >
                  <option value="">{t('consult.form.selectPlaceholder')}</option>
                  {contactMethods.map(method => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>
                  <span className="required">*</span>
                  {formData.contactMethod === 'email'
                    ? t('consult.form.emailAddress')
                    : t('consult.form.phoneNumber')}
                </Label>
                <Input
                  type={formData.contactMethod === 'email' ? 'email' : 'tel'}
                  name="contactInfo"
                  required
                  value={formData.contactInfo}
                  onChange={handleChange}
                  placeholder={formData.contactMethod === 'email'
                    ? t('consult.form.emailPlaceholder')
                    : t('consult.form.phonePlaceholder')}
                />
              </FormGroup>

              <FormGroup>
                <Label>{i18n.language === 'zh' ? '提供更多信息，让咨询更高效！' : 'Provide More Information for a More Effective Consultation!'}</Label>
                <TextArea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                />
              </FormGroup>

              <QuestionText>
                {t('consult.form.scheduleQuestion')}
              </QuestionText>

              <FormGroup>
                <Label>{t('consult.form.datetime')}</Label>
                <Input
                  type="datetime-local"
                  name="consultationTime"
                  value={formData.consultationTime}
                  onChange={handleChange}
                />
              </FormGroup>

              <FormGroup>
                <HCaptcha
                  sitekey="bd22e346-d31e-4a00-ba94-4fb04a04b43c"
                  onVerify={onVerify}
                  ref={captchaRef}
                />
              </FormGroup>

              <PrivacyText>
                {t('consult.form.privacy')}
              </PrivacyText>

              {!showSuccess ? (
                <SubmitButton type="submit">
                  {t('consult.form.submit')}
                </SubmitButton>
              ) : (
                <SuccessMessage>
                  {t('consult.form.success')}
                </SuccessMessage>
              )}
            </form>
          </FormContainer>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'space-between',
            gap: '2rem',
            marginBottom: '4rem'
          }}>
            {/* 邮件卡片 */}
            <div style={{
              flex: 1,
              backgroundColor: '#111111',
              borderRadius: '8px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}>
              <div style={{
                color: '#0F7E8D',
                fontSize: '2rem',
                marginBottom: '1rem',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                {/* 邮件图标 */}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48" fill="currentColor">
                  <path d="M20 4H4c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h16c1.103 0 2-.897 2-2V6c0-1.103-.897-2-2-2zm0 2v.511l-8 6.223-8-6.222V6h16zM4 18V9.044l7.386 5.745a.994.994 0 0 0 1.228 0L20 9.044 20.002 18H4z" />
                </svg>
              </div>
              <h3 style={{
                fontSize: '1.5rem',
                marginBottom: '1rem'
              }}>Email</h3>
              <p style={{
                color: '#A0AEC0'
              }}>info@cyberate.co.nz</p>
            </div>

            <div style={{
              flex: 1,
              backgroundColor: '#111111',
              borderRadius: '8px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}>
              <div style={{
                color: '#0F7E8D',
                fontSize: '2rem',
                marginBottom: '1rem',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                {/* 电话图标 */}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48" fill="currentColor">
                  <path d="M20 10.999h2C22 5.869 18.127 2 12.99 2v2C17.052 4 20 6.943 20 10.999z"></path>
                  <path d="M13 8c2.103 0 3 .897 3 3h2c0-3.225-1.775-5-5-5v2zm3.422 5.443a1.001 1.001 0 0 0-1.391.043l-2.393 2.461c-.576-.11-1.734-.471-2.926-1.66-1.192-1.193-1.553-2.354-1.66-2.926l2.459-2.394a1 1 0 0 0 .043-1.391L6.859 3.513a1 1 0 0 0-1.391-.087l-2.17 1.861a1 1 0 0 0-.29.649c-.015.25-.301 6.172 4.291 10.766C11.305 20.707 16.323 21 17.705 21c.202 0 .326-.006.359-.008a.992.992 0 0 0 .648-.291l1.86-2.171a1 1 0 0 0-.086-1.391l-4.064-3.696z"></path>
                </svg>
              </div>
              <h3 style={{
                fontSize: '1.5rem',
                marginBottom: '1rem'
              }}>Phone</h3>
              <p style={{
                color: '#A0AEC0'
              }}>+64 (09) 886 3999
              </p>
              {/* <p style={{
                color: '#A0AEC0'
              }}>
              International: +64 (09) 886 3999
              </p> */}
            </div>

            <div style={{
              width: '100%',
              height: '100%',
              flex: 1,
              borderRadius: '8px',
              overflow: 'hidden'
            }}>

              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d25541.065512598502!2d174.77694941269104!3d-36.85126007211657!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6d0d480f91933359%3A0x925dc4e3e2e6adba!2s17b%20Farnham%20Street%2C%20Parnell%2C%20Auckland%201052%2C%20New%20Zealand!5e0!3m2!1sen!2snz!4v1742370688193!5m2!1sen!2snz"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                zoom="16"
              />
            </div>



            {/* 地址卡片 */}
            <div style={{
              flex: 1,
              backgroundColor: '#111111',
              borderRadius: '8px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}>
              <div style={{
                color: '#0F7E8D',
                fontSize: '2rem',
                marginBottom: '1rem',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                {/* 地址图标 */}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48" fill="currentColor">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                </svg>
              </div>
              <h3 style={{
                fontSize: '1.5rem',
                marginBottom: '1rem'
              }}>Address</h3>
              <p style={{
                color: '#A0AEC0'
              }}>17b Farnham Street, Parnell, Auckland 1052, New Zealand</p>
            </div>
          </div>

          {/* Google Map */}

        </div>
      </div>
    </div>
  );
}

export default ContactForm; 